import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import SortableTable from '../SortableTable';
import { obfuscateUsername } from '../../pages/VisitsViewer';
import { isCountableVisit } from '../../constants/visitsClassification';
import { UNIVERS_ALL, visitsWithDuration } from '../../constants/visitsUnivers';

function VisitsSalespeople({ visits, dateRange, obfuscate, selectedUnivers = UNIVERS_ALL }) {

  // Calculer les statistiques par vendeur
  const salespeopleSummary = useMemo(() => {
    // Extraire les données par vendeur à partir des visites
    const salesMap = new Map();

    // Only real, held store visits count toward salesperson productivity.
    const countable = visits.filter(isCountableVisit);

    // Numerator and denominator from one population — see visitsWithDuration().
    const durationByUser = new Map();
    visitsWithDuration(countable, selectedUnivers).forEach((v) => {
      if (!v.date || !v.username) return;
      const acc = durationByUser.get(v.username) || { total: 0, count: 0 };
      acc.total += v.durationMinutes;
      acc.count += 1;
      durationByUser.set(v.username, acc);
    });

    countable.forEach(visit => {
      if (!visit.username) return;

      if (!salesMap.has(visit.username)) {
        salesMap.set(visit.username, {
          username: visit.username,
          totalVisits: 0,
          completedVisits: 0,
          uniqueDays: new Set(),
          uniqueWeeks: new Set(),
          uniqueStores: new Set(),
        });
      }

      const salesData = salesMap.get(visit.username);
      salesData.totalVisits++;

      if (visit.date) {
        salesData.completedVisits++;

        // Ajouter le point de vente aux magasins uniques
        if (visit.interventionPoint) {
          salesData.uniqueStores.add(visit.interventionPoint);
        }

        // Format pour le jour: YYYY-MM-DD
        const dayKey = format(visit.date, 'yyyy-MM-dd');
        salesData.uniqueDays.add(dayKey);

        // Format pour la semaine: YYYY-ww (année-numéro de semaine)
        const weekKey = format(visit.date, 'yyyy-ww', { locale: fr });
        salesData.uniqueWeeks.add(weekKey);
      }
    });

    // Calculer les moyennes pour chaque vendeur
    return Array.from(salesMap.values()).map(salesperson => {
      const totalDays = salesperson.uniqueDays.size;
      const totalWeeks = salesperson.uniqueWeeks.size;
      // Averaged over the visits that actually carry a duration, like the Résumé.
      const measured = durationByUser.get(salesperson.username) || { total: 0, count: 0 };
      const avgDuration = measured.count > 0
        ? Math.round(measured.total / measured.count)
        : 0;

      return {
        username: salesperson.username,
        totalVisits: salesperson.totalVisits,
        completedVisits: salesperson.completedVisits,
        visitsPerDay: totalDays > 0 ? (salesperson.completedVisits / totalDays).toFixed(1) : 'N/A',
        visitsPerWeek: totalWeeks > 0 ? (salesperson.completedVisits / totalWeeks).toFixed(1) : 'N/A',
        activeDays: totalDays,
        activeWeeks: totalWeeks,
        uniqueStores: salesperson.uniqueStores.size,
        avgDuration
      };
    }).sort((a, b) => b.completedVisits - a.completedVisits);
  }, [visits, selectedUnivers]);

  // Formatter la durée (minutes -> heures:minutes)
  const formatDuration = (minutes) => {
    if (!minutes) return "N/A";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
  };

  // Définir les colonnes pour le SortableTable
  const columns = useMemo(() => [
    {
      key: 'username',
      label: 'Personne',
      render: (item) => (
        <span className="font-medium">{obfuscate ? obfuscateUsername(item.username) : item.username}</span>
      )
    },
    {
      key: 'visitsRatio',
      label: 'Visites réalisées',
      render: (item, isRawValue) => {
        if (isRawValue) return item.completedVisits / Math.max(1, item.totalVisits);
        return (
          <div className="text-center">
            {item.completedVisits} / {item.totalVisits}
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {item.totalVisits > 0 ? Math.round((item.completedVisits / item.totalVisits) * 100) : 0}%
            </div>
          </div>
        );
      },
      sortFunction: (a, b, direction) => {
        // Calculer les pourcentages de visites réalisées
        const aPercent = a.totalVisits > 0 ? Math.round((a.completedVisits / a.totalVisits) * 100) : 0;
        const bPercent = b.totalVisits > 0 ? Math.round((b.completedVisits / b.totalVisits) * 100) : 0;

        // Si les pourcentages sont égaux, trier par nombre total de visites complétées
        if (aPercent === bPercent) {
          return direction === 'ascending' ?
            a.completedVisits - b.completedVisits :
            b.completedVisits - a.completedVisits;
        }

        return direction === 'ascending' ? aPercent - bPercent : bPercent - aPercent;
      }
    },
    {
      key: 'uniqueStores',
      label: 'Magasins visités',
      render: (item, isRawValue) => {
        if (isRawValue) return item.uniqueStores;
        return <div className="text-center">{item.uniqueStores}</div>;
      }
    },
    {
      key: 'visitsPerDay',
      label: 'Visites / jour',
      render: (item, isRawValue) => {
        if (isRawValue) return typeof item.visitsPerDay === 'string' ? 0 : parseFloat(item.visitsPerDay);
        return <div className="text-center">{item.visitsPerDay}</div>;
      },
      sortFunction: (a, b, direction) => {
        const aValue = typeof a.visitsPerDay === 'string' ? 0 : parseFloat(a.visitsPerDay);
        const bValue = typeof b.visitsPerDay === 'string' ? 0 : parseFloat(b.visitsPerDay);
        return direction === 'ascending' ? aValue - bValue : bValue - aValue;
      }
    },
    {
      key: 'visitsPerWeek',
      label: 'Visites / semaine',
      render: (item, isRawValue) => {
        if (isRawValue) return typeof item.visitsPerWeek === 'string' ? 0 : parseFloat(item.visitsPerWeek);
        return <div className="text-center">{item.visitsPerWeek}</div>;
      },
      sortFunction: (a, b, direction) => {
        const aValue = typeof a.visitsPerWeek === 'string' ? 0 : parseFloat(a.visitsPerWeek);
        const bValue = typeof b.visitsPerWeek === 'string' ? 0 : parseFloat(b.visitsPerWeek);
        return direction === 'ascending' ? aValue - bValue : bValue - aValue;
      }
    },
    {
      key: 'avgDuration',
      label: 'Durée moyenne',
      render: (item, isRawValue) => {
        if (isRawValue) return item.avgDuration;
        return <div className="text-center">{formatDuration(item.avgDuration)}</div>;
      }
    },
    {
      key: 'activeDays',
      label: 'Jours actifs',
      render: (item, isRawValue) => {
        if (isRawValue) return item.activeDays;
        return <div className="text-center">{item.activeDays} j / {item.activeWeeks} sem</div>;
      }
    }
  ], []);

  return (
    <div className="space-y-6">
      {/* Remplacer le tableau par SortableTable */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Performance de l'effectif</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Période: {dateRange}
          </p>
        </div>

        <SortableTable
          columns={columns}
          data={salespeopleSummary}
          emptyMessage="Aucune donnée disponible pour la période sélectionnée."
          countLabel={`Total : ${salespeopleSummary.length} personnes`}
          disableFilters={true} // Désactiver les filtres
          initialSortBy={{
            key: 'completedVisits',
            direction: 'descending'
          }}
        />
      </div>
    </div>
  );
}

export default VisitsSalespeople;